import { useEffect, useCallback, useRef } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import ErrorBoundary from '@/components/ErrorBoundary'
import Navbar from '@/components/Navbar'
import BackToTop from '@/components/BackToTop'
import ShortcutsHelp from '@/components/ShortcutsHelp'
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts'
import { useAuthStore } from '@/stores/authStore'

/**
 * F3.A11Y-3 fix: Handle the skip-link click manually so we can:
 *  1. Move keyboard focus to <main> (otherwise screen readers don't know
 *     we "skipped" past the navbar — only the scroll moved).
 *  2. Remove the URL hash so #main-content doesn't end up in the address bar
 *     (which would break the back button and look ugly).
 *  3. Push the focus to the first focusable child if one exists, so a
 *     keyboard user lands directly in the first interactive element
 *     (e.g. textarea in Feed) rather than on the page chrome.
 */
function SkipLink() {
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (target.tagName !== 'A') return
      const a = target as HTMLAnchorElement
      if (a.getAttribute('href') !== '#main-content') return
      e.preventDefault()
      const main = document.getElementById('main-content')
      if (!main) return
      // Try to focus the first focusable element inside <main>
      const focusable = main.querySelector<HTMLElement>(
        'input, textarea, select, button, a[href], [tabindex]:not([tabindex="-1"])',
      )
      const targetEl = focusable || main
      targetEl.focus({ preventScroll: false })
      // Smooth scroll to top of main
      main.scrollIntoView({ behavior: 'smooth', block: 'start' })
      // Remove the hash from the URL
      if (window.history && window.history.replaceState) {
        const { pathname, search } = window.location
        window.history.replaceState(null, '', pathname + search)
      }
    }
    document.addEventListener('click', handler)
    return () => document.removeEventListener('click', handler)
  }, [])
  return null
}

/**
 * Keyboard shortcut policy (revised — single-letter shortcuts removed):
 *
 *   Single-key shortcuts (m, n, f, h) on bare letters are dangerous:
 *   a user typing a message that contains "m" or "n" would suddenly
 *   navigate away. So we DON'T bind them on bare presses.
 *
 *   Instead we expose:
 *     - Cmd/Ctrl + K         → Search
 *     - Cmd/Ctrl + /         → Search (alt binding for non-US keyboards)
 *     - Cmd/Ctrl + Shift + N → Focus new-post composer
 *     - /                     → Focus search bar (GitHub/Twitter convention)
 *     - g then f (within 1s) → Feed       (Vim-style, opt-in)
 *     - g then m (within 1s) → Messages
 *     - g then n (within 1s) → Notifications
 *     - g then h (within 1s) → Home
 *     - g then s (within 1s) → Settings
 *     - ?                     → Show keyboard shortcut help
 *
 *   All single-key shortcuts ignore input/textarea/contenteditable
 *   (handled by `ignoreInputs: true`).
 */
export default function Layout() {
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated } = useAuthStore()

  const openSearch = useCallback(() => navigate('/search'), [navigate])
  const goHome = useCallback(() => navigate('/'), [navigate])
  const goNewPost = useCallback(() => {
    const composer = document.querySelector<HTMLTextAreaElement>('textarea[name="message"], textarea#message')
    composer?.focus()
  }, [])

  // Modifier-required shortcuts (always safe)
  useKeyboardShortcuts([
    { key: 'k', meta: true, handler: openSearch, ignoreInputs: true },
    { key: '/', meta: true, handler: openSearch, ignoreInputs: true },
    { key: 'n', meta: true, shift: true, handler: goNewPost, ignoreInputs: true },
    { key: 'h', meta: true, shift: true, handler: goHome, ignoreInputs: true },
    { key: '/', handler: openSearch, ignoreInputs: true },
    { key: '?', handler: () => window.dispatchEvent(new CustomEvent('app:show-shortcuts')), ignoreInputs: true, shift: true },
  ])

  // Vim-style "g then X" sequence with 1s timeout
  const pendingG = useRef<number | null>(null)

  useEffect(() => {
    if (!isAuthenticated) return

    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return
      if (e.metaKey || e.ctrlKey || e.altKey) return

      const key = e.key.toLowerCase()

      if (pendingG.current !== null) {
        const second = key
        pendingG.current = null
        const map: Record<string, string> = {
          f: '/feed',
          m: '/messages',
          n: '/notifications',
          h: '/',
          s: '/settings',
        }
        const dest = map[second]
        if (dest) {
          e.preventDefault()
          navigate(dest)
        }
        return
      }

      if (key === 'g') {
        pendingG.current = Date.now()
        setTimeout(() => {
          if (pendingG.current && Date.now() - pendingG.current >= 1000) {
            pendingG.current = null
          }
        }, 1100)
      }
    }

    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isAuthenticated, navigate])

  // When the route changes, move focus to <main> so screen-reader users
  // hear the new page (without this, focus stays on whatever link was clicked).
  useEffect(() => {
    const main = document.getElementById('main-content')
    if (main) {
      // Only steal focus if focus was already on a link/button
      const active = document.activeElement as HTMLElement | null
      const wasInteractive = active && (active.tagName === 'A' || active.tagName === 'BUTTON')
      if (wasInteractive) {
        main.focus({ preventScroll: false })
      }
      // Always scroll to top on route change
      window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior })
    }
  }, [location.pathname])

  return (
    <ErrorBoundary>
      <SkipLink />
      <div className="min-h-screen">
        <Navbar />
        <main
          id="main-content"
          className="p-4 md:p-8 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
          tabIndex={-1}
        >
          <Outlet />
        </main>
        <BackToTop />
        <ShortcutsHelp />
      </div>
    </ErrorBoundary>
  )
}
